import React, { useState, useEffect } from "react";
import adminService from "services/adminService";
import orgDataService from "services/orgDataService";
import { Hub } from "common/models";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

interface DispatchQueueConfig {
  dispatchQueueConfigId: number;
  name: string;
  active: boolean;
  hubs: { hubId: number; hubName: string }[];
}

const DispatchQueuesList = () => {
  const [queues, setQueues] = useState<DispatchQueueConfig[]>([]);
  const [newQueueName, setNewQueueName] = useState<string>("");
  const [selectedHubIds, setSelectedHubIds] = useState<number[]>([]);
  const [allHubs, setAllHubs] = useState<Hub[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const navigate = useNavigate();

  useEffect(() => {
    loadQueues();
    loadHubs();
  }, [activeFilter]);

  const loadHubs = async () => {
    try {
      const hubs = await orgDataService.getAllHubs();
      setAllHubs(hubs);
    } catch (error) {
      toast.error("Failed to load hubs");
    }
  };

  const loadQueues = () => {
    const active = activeFilter === "all" ? undefined : activeFilter === "active";
    adminService.getDispatchQueues(active).then((results) => {
      setQueues(results);
    });
  };

  const handleOpenModal = () => {
    setNewQueueName("");
    setSelectedHubIds([]);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setNewQueueName("");
    setSelectedHubIds([]);
  };

  const createQueue = async () => {
    if (!newQueueName.trim()) {
      toast.error("Please enter a queue name");
      return;
    }
    try {
      await adminService.createDispatchQueue(newQueueName, selectedHubIds);
     
      loadQueues();
      toast.success("Dispatch Queue created successfully");
    } catch (error) {
      toast.error("Failed to create Dispatch Queue");
    } finally {
       handleCloseModal();
    }
  };

  const toggleActive = async (queue: DispatchQueueConfig) => {
    try {
      await adminService.updateDispatchQueue(queue.dispatchQueueConfigId, queue.name, !queue.active);
      loadQueues();
      toast.success(`Dispatch Queue ${!queue.active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error("Failed to update Dispatch Queue");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4">Dispatch Queues</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              label="Status"
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <MDButton variant="contained" color="primary" onClick={handleOpenModal}>
            Create Dispatch Queue
          </MDButton>
        </Box>
      </Box>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Create New Dispatch Queue
          <MDButton
            variant="text"
            color="secondary"
            onClick={handleCloseModal}
            sx={{ minWidth: "auto", p: 1 }}
          >
            ✕
          </MDButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Queue Name"
              value={newQueueName}
              onChange={(e) => setNewQueueName(e.target.value)}
              fullWidth
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Select Hubs</InputLabel>
              <Select
                multiple
                value={selectedHubIds}
                onChange={(e) => setSelectedHubIds(e.target.value as number[])}
                input={<OutlinedInput label="Select Hubs" />}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((hubId) => {
                      const hub = allHubs.find(h => h.id === hubId);
                      return <Chip key={hubId} label={hub?.name || `Hub ${hubId}`} size="small" />;
                    })}
                  </Box>
                )}
              >
                {allHubs.map((hub) => (
                  <MenuItem key={hub.id} value={hub.id}>
                    {hub.name || `Hub ${hub.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" onClick={handleCloseModal}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="primary" onClick={createQueue}>
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Hubs</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queues.map((queue) => (
              <TableRow key={queue.dispatchQueueConfigId}>
                <TableCell>{queue.name}</TableCell>
                <TableCell>
                  {queue.hubs?.map((h) => h.hubName).join(", ") || "No hubs assigned"}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={queue.active}
                    onChange={() => toggleActive(queue)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => navigate(`/admin/dispatch-queue/${queue.dispatchQueueConfigId}`)}
                  >
                    Edit Hubs
                  </MDButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
};

export default DispatchQueuesList;
